import { useCallback, useEffect, useState } from 'react'
import { BellRing, Copy, History, Pause, Play, Plus, Trash2 } from 'lucide-react'
import { Shell, useShellMeta } from './Shell.js'
import { ToastProvider, useToast, apiGet, adminMutate, usePermissions } from './primitives.js'
import { ConfirmDialog } from '../auth/Dialog.js'
import { navigate } from '../router.js'

/**
 * /alerts/rules — GESTION DES RÈGLES (LOT 9).
 *
 * Doctrine : AUCUNE règle n'existe d'office. « Gestion me prévient
 * exactement comme JE le décide. » Les modèles de départ sont des
 * suggestions — rien n'est activé sans action humaine.
 */

export interface RuleListItem {
  id: string
  kind: 'SYSTEM' | 'USER'
  name: string
  description: string | null
  is_enabled: boolean
  service_slug: string
  metric_key: string
  operator_label: string
  threshold: unknown
  duration_s: number
  severity: string
  severity_label: string
  runtime_state: string
  active_instance_id: string | null
  last_fired_at: string | null
  instance_count: number
  recipients_labels: string[]
}

export function condSummary(r: RuleListItem): string {
  const dur = r.duration_s >= 3600 ? `${Math.round(r.duration_s / 3600)} h` : r.duration_s >= 60 ? `${Math.round(r.duration_s / 60)} min` : r.duration_s > 0 ? `${r.duration_s} s` : null
  const val = r.threshold === null || r.threshold === undefined ? '' : ` ${String(r.threshold)}`
  return `${r.metric_key === 'service.status' ? 'État' : r.metric_key} ${r.operator_label}${val}${dur ? ` · ${dur}` : ''}`
}

const RT_LABEL: Record<string, string> = {
  IDLE: '', PENDING: 'Condition en attente', FIRING: 'Incident en cours', RECOVERING: 'Revenue à la normale — clôture manuelle',
}

function Inner() {
  const meta = useShellMeta()
  const toast = useToast()
  const { permissions, authenticated } = usePermissions()
  const canWrite = authenticated && permissions.includes('rule.write')
  const [items, setItems] = useState<RuleListItem[] | null>(null)
  const [toDelete, setToDelete] = useState<RuleListItem | null>(null)

  const load = useCallback(() => {
    apiGet<{ items: RuleListItem[] }>('/api/v1/alert-rules').then((j) => setItems(j.items)).catch(() => undefined)
  }, [])
  useEffect(() => { load() }, [load])

  const act = async (path: string, okMsg: string) => {
    const r = await adminMutate<{ error?: string; message?: string }>('POST', path)
    if (r.status < 300) { toast('ok', okMsg); load() }
    else toast('error', r.data.message ?? 'L’action a échoué — rien n’a changé.')
  }

  const doDelete = async () => {
    if (!toDelete) return
    const r = await adminMutate<{ error?: string; message?: string }>('DELETE', `/api/v1/alert-rules/${toDelete.id}`)
    if (r.status < 300) { toast('ok', 'Règle supprimée — son historique d’incidents est conservé.'); setToDelete(null); load() }
    else toast('error', r.data.message ?? 'La suppression a échoué.')
  }

  return (
    <Shell active="alerts" meta={meta}>
      <main className="lc-page lc-stagger">
        <div className="adm-head">
          <div>
            <h1 className="lc-title">Règles d’alerte</h1>
            <p className="lc-subtitle">
              Quand une donnée devient-elle importante ? C’est vous qui le décidez —
              Gestion n’impose aucune règle.
            </p>
          </div>
          {canWrite ? (
            <button className="lc-btn lc-btn--primary" onClick={() => navigate('/alerts/rules/new')}>
              <Plus size={15} aria-hidden="true" /> Nouvelle règle
            </button>
          ) : null}
        </div>

        {items?.length === 0 ? (
          <section className="lc-panel gd-panel rb-empty">
            <BellRing size={28} aria-hidden="true" className="rb-empty__icon" />
            <h2 className="lc-section-title">Aucune règle personnalisée</h2>
            <p className="lc-hint">
              Créez votre première règle — par exemple « Service indisponible pendant 5 minutes »
              ou « Flux vidéo hors ligne ». Le builder vous guide en 8 étapes.
            </p>
            {canWrite ? (
              <button className="lc-btn lc-btn--primary" onClick={() => navigate('/alerts/rules/new')}>
                <Plus size={15} aria-hidden="true" /> Créer une règle
              </button>
            ) : null}
          </section>
        ) : null}

        {items && items.length > 0 ? (
          <section className="lc-panel gd-panel" aria-label="Règles">
            <div className="lc-panel__body lc-panel__body--flush">
              {items.map((r) => (
                <div key={r.id} className="lc-action-row rb-rule-row" role="button" tabIndex={0}
                  onClick={() => navigate(`/alerts/rules/${r.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/alerts/rules/${r.id}`) }}>
                  <span className={`lc-dot ${!r.is_enabled ? 'lc-dot--idle' : r.runtime_state === 'FIRING' ? 'lc-dot--critical' : r.runtime_state === 'PENDING' ? 'lc-dot--warning' : 'lc-dot--ok lc-dot--live'}`} aria-hidden="true" />
                  <div className="lc-action-row__body">
                    <div className="lc-action-row__title">
                      {r.name}
                      {r.kind === 'SYSTEM' ? <span className="lc-pill lc-pill--idle" style={{ marginLeft: 8 }}>Système</span> : null}
                      {!r.is_enabled ? <span className="lc-pill lc-pill--idle" style={{ marginLeft: 8 }}>Désactivée</span> : null}
                    </div>
                    <div className="lc-action-row__meta">
                      {r.service_slug} · {condSummary(r)} · {r.severity_label}
                      {r.recipients_labels.length > 0 ? ` · ${r.recipients_labels.join(', ')}` : ' · aucun destinataire'}
                      {RT_LABEL[r.runtime_state] ? ` · ${RT_LABEL[r.runtime_state]}` : ''}
                    </div>
                  </div>
                  {canWrite && r.kind === 'USER' ? (
                    <span className="rb-rule-row__actions" onClick={(e) => e.stopPropagation()}>
                      <button className="lc-btn lc-btn--ghost" data-tip="Historique"
                        onClick={() => navigate(`/alerts/rules/${r.id}?tab=history`)} aria-label="Historique">
                        <History size={14} aria-hidden="true" />
                      </button>
                      <button className="lc-btn lc-btn--ghost" data-tip="Dupliquer" aria-label="Dupliquer"
                        onClick={() => act(`/api/v1/alert-rules/${r.id}/duplicate`, 'Règle dupliquée (désactivée) — à vous de l’ajuster puis l’activer.')}>
                        <Copy size={14} aria-hidden="true" />
                      </button>
                      <button className="lc-btn lc-btn--ghost" data-tip={r.is_enabled ? 'Désactiver' : 'Réactiver'}
                        aria-label={r.is_enabled ? 'Désactiver' : 'Réactiver'}
                        onClick={() => act(`/api/v1/alert-rules/${r.id}/${r.is_enabled ? 'disable' : 'enable'}`,
                          r.is_enabled ? 'Règle désactivée — son incident ouvert éventuel est clos.' : 'Règle réactivée.')}>
                        {r.is_enabled ? <Pause size={14} aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}
                      </button>
                      <button className="lc-btn lc-btn--ghost" data-tip="Supprimer" aria-label="Supprimer" onClick={() => setToDelete(r)}>
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <ConfirmDialog
          open={toDelete !== null}
          danger
          title="Supprimer cette règle ?"
          confirmLabel="Supprimer"
          onConfirm={() => void doDelete()}
          onClose={() => setToDelete(null)}
        >
          « {toDelete?.name} » ne surveillera plus rien. Son incident ouvert éventuel sera clos
          (raison : règle supprimée) et l’historique de ses incidents est conservé.
        </ConfirmDialog>
      </main>
    </Shell>
  )
}

export function RulesPage() {
  return <ToastProvider><Inner /></ToastProvider>
}
